import React, { useEffect, useRef } from "react";
import Header from "./header";
import Footer from "./Footer";
import { Container } from "react-bootstrap";


export default function Home() {
    const pageRef = useRef(null);
    const statsRef = useRef(null);

    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;


        function animateCount(el) {
            const to = parseFloat(el.getAttribute("data-count-to"));
            const suffix = el.getAttribute("data-suffix") || "";
            const decimals = parseInt(el.getAttribute("data-decimal") || "0", 10);
            if (reduceMotion) {
                el.textContent = to.toFixed(decimals) + suffix;
                return;
            }
            let start = null;
            const duration = 1200;
            function step(ts) {
                if (!start) start = ts;
                const progress = Math.min((ts - start) / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                const val = to * eased;
                el.textContent = val.toFixed(decimals) + suffix;
                if (progress < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
        }

        const revealEls = document.querySelectorAll(".reveal");
        const countEls = document.querySelectorAll("[data-count-to]");

        let io;
        let countIO;

        if ("IntersectionObserver" in window && !reduceMotion) {
            io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add("visible");
                            io.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
            );
            revealEls.forEach((el) => io.observe(el));

            const statsBlock = statsRef.current;
            if (statsBlock) {
                countIO = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                countEls.forEach(animateCount);
                                countIO.disconnect();
                            }
                        });
                    },
                    { threshold: 0.4 }
                );
                countIO.observe(statsBlock);
            }
        } else {
            revealEls.forEach((el) => el.classList.add("visible"));
            countEls.forEach(animateCount);
        }


        const pageSection = pageRef.current;
        const blobs = pageSection ? pageSection.querySelectorAll(":scope > .home-blob") : [];
        let onMouseMove;
        if (
            pageSection &&
            blobs.length &&
            !reduceMotion &&
            window.matchMedia("(pointer: fine)").matches
        ) {
            onMouseMove = (e) => {
                const rect = pageSection.getBoundingClientRect();
                const cx = (e.clientX - rect.left) / rect.width - 0.5;
                const cy = (e.clientY - rect.top) / rect.height - 0.5;
                blobs.forEach((blob, i) => {
                    const strength = 18 + i * 6;
                    blob.style.translate = `${cx * strength}px ${cy * strength}px`;
                });
            };
            pageSection.addEventListener("mousemove", onMouseMove, { passive: true });
        }

        return () => {
            if (io) io.disconnect();
            if (countIO) countIO.disconnect();
            if (pageSection && onMouseMove) pageSection.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    return (
        <>
            <Container fluid></Container>

            <Header />
            {/* ---------- page ---------- */}
            <section className="home-page" ref={pageRef}>
                <div className="home-blob home-blob1" aria-hidden="true"></div>
                <div className="home-blob home-blob2" aria-hidden="true"></div>
                <div className="home-blob home-blob3" aria-hidden="true"></div>

                <div className="home-page-inner">

                    {/* Left Content */}
                    <div>
                        <span
                            className="home-pill page-anim"
                            style={{ "--d": ".05s" }}
                        >
                            <span className="home-dot">✓</span>
                            Explore Global Investment Opportunities
                        </span>

                        <h1
                            className="page-anim"
                            style={{ "--d": ".15s" }}
                        >
                            Invest smarter.
                            <br />
                            Build your <span className="home-accent">future.</span>
                        </h1>

                        <p
                            className="home-lede page-anim"
                            style={{ "--d": ".28s" }}
                        >
                            Global Inavation brings diverse investment opportunities
                            together in one place. Explore crypto, shares, precious
                            metals, currencies, and more as you build towards your
                            financial goals.
                        </p>

                        <div
                            className="home-page-ctas page-anim"
                            style={{ "--d": ".4s" }}
                        >
                            <a
                                href="/register"
                                className="btn-global btn-primary-global btn-lg-global"
                            >
                                Get Started
                            </a>

                            <a
                                href="#how-it-works"
                                className="btn-global btn-ghost-dark-global btn-lg-global"
                            >
                                Explore Opportunities
                            </a>
                        </div>
                    </div>


                    {/* Right Dashboard */}
                    <div className="home-dash">

                        <div className="home-float-tag home-t1">
                            <span className="home-ico">↑</span>
                            Global Market Opportunities
                        </div>


                        <div className="home-dash-card">

                            <div className="home-dash-head">
                                <span>Market Overview</span>

                                <span className="home-badge">
                                    Live
                                </span>
                            </div>


                            <div className="home-dash-body">

                                <div className="home-big-num">
                                    Explore Markets
                                </div>

                                <div className="home-big-num-sub">
                                    Discover opportunities across multiple investment categories
                                </div>


                                <div className="home-mini-rows">

                                    {/* Crypto */}
                                    <div className="home-mini-row">

                                        <div className="home-mini-row-left">
                                            <span
                                                className="home-chip"
                                                style={{ background: "var(--coral)" }}
                                            >
                                                ₿
                                            </span>

                                            Crypto Assets
                                        </div>

                                        <span className="home-amt">
                                            Explore
                                        </span>

                                    </div>


                                    {/* Shares */}
                                    <div className="home-mini-row">

                                        <div className="home-mini-row-left">
                                            <span
                                                className="home-chip"
                                                style={{ background: "var(--mint-deep)" }}
                                            >
                                                ↗
                                            </span>

                                            Shares & Stocks
                                        </div>

                                        <span className="home-amt">
                                            Explore
                                        </span>

                                    </div>


                                    {/* Gold */}
                                    <div className="home-mini-row">

                                        <div className="home-mini-row-left">
                                            <span
                                                className="home-chip"
                                                style={{ background: "#8B7CF6" }}
                                            >
                                                ★
                                            </span>

                                            Gold & Silver
                                        </div>

                                        <span className="home-amt">
                                            Explore
                                        </span>

                                    </div>

                                </div>
                            </div>
                        </div>


                        <div className="home-float-tag home-t2">
                            <span className="home-ico">⚡</span>
                            Explore. Invest. Grow.
                        </div>

                    </div>
                </div>
            </section>


            {/* ---------- Market Categories Marquee ---------- */}
            <div className="home-logos">

                <div className="home-marquee">

                    {[...Array(2)].flatMap((_, dup) =>
                        [
                            "Crypto",
                            "Shares",
                            "Gold",
                            "Silver",
                            "Currency",
                            "Global Markets"
                        ].map((name) => (

                            <span
                                className="home-wordmark"
                                key={`${name}-${dup}`}
                            >
                                {name}
                            </span>

                        ))
                    )}

                </div>

            </div>

            {/* ---------- Features (bento) ---------- */}
            <section id="features">
                <div className="wrap">

                    {/* Welcome Section */}
                    <div className="home-feature-intro">

                        {/* Left Content */}
                        <div className="section-head reveal home-feature-content">

                            <h2>
                                Welcome to the Future of Wealth Creation!
                            </h2>

                            <p>
                                We are a leading crypto investment platform dedicated to
                                helping individuals and businesses grow their wealth through
                                secure, transparent, and profitable digital asset investments.
                                With a team of blockchain experts, financial analysts, and
                                technology professionals, we provide innovative solutions
                                that make crypto investing easy, accessible, and rewarding
                                for everyone.

                                <br />
                                <br />

                                At the core of our mission is trust, security, and financial
                                freedom. We empower our investors with expert guidance,
                                automated investment strategies, and high-yield opportunities,
                                helping you explore investment opportunities with confidence.
                            </p>

                        </div>


                        {/* Right SVG */}
                        <div className="home-feature-svg reveal">

                            <svg
                                viewBox="0 0 500 500"
                                width="100%"
                                height="100%"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                {/* Background Circle */}
                                <circle
                                    cx="250"
                                    cy="250"
                                    r="210"
                                    fill="var(--mint)"
                                    opacity="0.08"
                                />

                                {/* Outer Orbit */}
                                <circle
                                    cx="250"
                                    cy="250"
                                    r="175"
                                    stroke="var(--mint-deep)"
                                    strokeWidth="2"
                                    strokeDasharray="10 12"
                                    opacity="0.5"
                                />

                                {/* Inner Orbit */}
                                <circle
                                    cx="250"
                                    cy="250"
                                    r="125"
                                    stroke="var(--coral)"
                                    strokeWidth="2"
                                    opacity="0.35"
                                />

                                {/* Main Crypto Coin */}
                                <circle
                                    cx="250"
                                    cy="250"
                                    r="78"
                                    fill="var(--coral)"
                                />

                                <circle
                                    cx="250"
                                    cy="250"
                                    r="62"
                                    stroke="white"
                                    strokeWidth="2"
                                    opacity="0.4"
                                />

                                {/* Bitcoin Symbol */}
                                <text
                                    x="250"
                                    y="280"
                                    textAnchor="middle"
                                    fill="white"
                                    fontSize="88"
                                    fontWeight="700"
                                    fontFamily="Arial"
                                >
                                    ₿
                                </text>


                                {/* Growth Chart */}
                                <path
                                    d="M100 370 L170 310 L225 335 L300 245 L390 150"
                                    stroke="var(--mint-deep)"
                                    strokeWidth="7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />

                                {/* Arrow */}
                                <path
                                    d="M365 150 H390 V175"
                                    stroke="var(--mint-deep)"
                                    strokeWidth="7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />


                                {/* Floating Coin - Dollar */}
                                <circle
                                    cx="120"
                                    cy="165"
                                    r="34"
                                    fill="var(--mint-deep)"
                                    opacity="0.9"
                                />

                                <text
                                    x="120"
                                    y="176"
                                    textAnchor="middle"
                                    fill="white"
                                    fontSize="32"
                                    fontWeight="bold"
                                >
                                    $
                                </text>


                                {/* Floating Coin - Growth */}
                                <circle
                                    cx="395"
                                    cy="325"
                                    r="32"
                                    fill="var(--coral)"
                                    opacity="0.9"
                                />

                                <text
                                    x="395"
                                    y="336"
                                    textAnchor="middle"
                                    fill="white"
                                    fontSize="30"
                                    fontWeight="bold"
                                >
                                    ↗
                                </text>


                                {/* Small decorative nodes */}
                                <circle
                                    cx="170"
                                    cy="90"
                                    r="8"
                                    fill="var(--coral)"
                                />

                                <circle
                                    cx="360"
                                    cy="100"
                                    r="7"
                                    fill="var(--mint-deep)"
                                />

                                <circle
                                    cx="430"
                                    cy="220"
                                    r="7"
                                    fill="var(--coral)"
                                />

                                <circle
                                    cx="80"
                                    cy="290"
                                    r="6"
                                    fill="var(--mint-deep)"
                                />

                            </svg>

                        </div>

                    </div>


                    <div className="home-bento">
                        {/* Main Card */}
                        <div
                            className="home-card home-c1 reveal"
                            style={{ "--d": "0s" }}
                        >
                            <div className="home-c1-visual">
                                <span></span>
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>

                            <div className="home-bento-icon">
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <path d="M3 3v18h18M8 17V9m5 8V5m5 12v-4" />
                                </svg>
                            </div>

                            <h3>Smart Investment Opportunities</h3>

                            <p>
                                Explore powerful investment opportunities across digital assets,
                                shares, precious metals, and global markets.
                            </p>
                        </div>

                        {/* Crypto */}
                        <div
                            className="home-card home-c2 reveal"
                            style={{ "--d": ".08s" }}
                        >
                            <div className="home-bento-icon">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M12 6v12M9 9h4a2 2 0 010 4H11a2 2 0 000 4h4" />
                                </svg>
                            </div>

                            <h3>Crypto Trading</h3>

                            <p>
                                Explore digital asset opportunities and grow your portfolio with
                                market insights.
                            </p>
                        </div>

                        {/* Shares */}
                        <div
                            className="home-card home-c3 reveal"
                            style={{ "--d": ".16s" }}
                        >
                            <div className="home-bento-icon">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <path d="M3 17l6-6 4 4 8-9" />
                                    <path d="M15 6h6v6" />
                                </svg>
                            </div>

                            <h3>Shares Trading</h3>

                            <p>
                                Discover opportunities in leading companies and build a diversified
                                investment portfolio.
                            </p>
                        </div>

                        {/* Gold */}
                        <div
                            className="home-card home-c4 reveal"
                            style={{ "--d": ".08s" }}
                        >
                            <div className="home-bento-icon">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <path d="M6 8h12l2 10H4L6 8z" />
                                    <path d="M8 8l1-3h6l1 3" />
                                </svg>
                            </div>

                            <h3>Gold & Silver</h3>

                            <p>
                                Diversify your investments with precious metals for long-term
                                value and stability.
                            </p>
                        </div>

                        {/* Currency */}
                        <div
                            className="home-card home-c5 reveal"
                            style={{ "--d": ".16s" }}
                        >
                            <div className="home-bento-icon">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M8 12h8M12 8l4 4-4 4" />
                                </svg>
                            </div>

                            <h3>Currency Trading</h3>

                            <p>
                                Access global market opportunities and take advantage of currency
                                market movements.
                            </p>
                        </div>
                    </div>

                </div>
            </section>

            {/* ---------- How it works ---------- */}
            <section className="home-how my-5 p-5" id="how-it-works">
                <div className="wrap">
                    <div className="section-head reveal" style={{ maxWidth: 640 }}>
                        <span className="section-eyebrow" style={{ color: "var(--gold)" }}>● How It Works</span>
                        <h2 style={{ color: "#fff" }}>Earn in three steps.</h2>

                    </div>

                    <div className="home-steps">
                        <div className="home-step reveal" style={{ "--d": "0s" }}>
                            <div className="home-step-num">1</div>
                            <h3>Sign up, It's Free!</h3>
                            <p>Create your account in minutes and start your crypto investment journey!</p>
                        </div>
                        <div className="home-step reveal" style={{ "--d": ".12s" }}>
                            <div className="home-step-num">2</div>
                            <h3>Find best Deals and Invest</h3>
                            <p>Explore top investment opportunities and choose the plan that suits your financial goals.</p>
                        </div>
                        <div className="home-step reveal" style={{ "--d": ".24s" }}>
                            <div className="home-step-num">3</div>
                            <h3>Get you profit back</h3>
                            <p>Earn high returns and withdraw your profits securely and instantly! 🚀</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- Quote ---------- */}
            <section className="home-quote-section m-5" id="customers">
                <div className="wrap">
                    <div className="home-quote-card reveal">
                        <div
                            className="home-blob home-blob1"
                            style={{
                                top: -120,
                                right: -80,
                                width: 320,
                                height: 320
                            }}
                        ></div>

                        <blockquote className="home-blockquote">
                            Global Inavation gives investors access to diverse market
                            opportunities and a smarter way to explore the future of
                            investing.
                        </blockquote>

                        <div className="home-quote-attr">
                            <div className="home-quote-avatar">GI</div>

                            <div>
                                <div className="home-name">
                                    Global Inavation
                                </div>

                                <div className="home-role">
                                    Empowering Your Financial Future
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>



            {/* ---------- CTA band ---------- */}

            <div className="home-cta-band">
                <div className="wrap">
                    <div>
                        <h2>Ready to Explore New Opportunities?</h2>

                        <p>
                            Join Global Inavation today and explore opportunities across
                            global markets, investments, and financial growth.
                        </p>
                    </div>

                    <a
                        href="/register"
                        className="btn-global btn-dark-global btn-lg-global"
                    //   style={{ color: "white" }}
                    >
                        Create Account
                    </a>
                </div>
            </div>
            <Footer />

        </>
    );
}

